import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  createSettingItem,
  deleteSettingItem,
  fetchSettings,
  selectSettingsGroup,
  updateSettingItem,
} from '@main/features/settings/settingsSlice';
import { UiButton, UiCheckbox, UiInput, UiNativeSelect } from '@shared/common/ui';

const STATUSES_GROUP = 'client_statuses';
const AUTOMATIONS_GROUP = 'funnel_automations';
const WIDGETS_GROUP = 'funnel_widgets';
const TRIALS_WIDGET_KEY = 'trials_today';

const SYSTEM_TYPES = [
  { value: 'lead', label: 'Новый / лид' },
  { value: 'trial', label: 'Пробный этап' },
  { value: 'active', label: 'Активный' },
  { value: 'paused', label: 'Пауза' },
  { value: 'left', label: 'Отток' },
  { value: 'archive', label: 'Архив' },
];

const SYSTEM_TYPE_LABEL = Object.fromEntries(SYSTEM_TYPES.map((it) => [it.value, it.label]));

/**
 * Вид правила определяется событием (ТЗ «Дата пробного», §14):
 * событийные реагируют на действие в CRM, scheduled запускаются по времени.
 */
const EVENT_TYPES = [
  { value: 'FIRST_ATTENDANCE', label: 'Первое посещение направления', kind: 'event' },
  { value: 'CLIENT_STATUS_DISCOUNT', label: 'Постоянная скидка по статусу', kind: 'status_discount' },
  { value: 'DAILY_TRIALS_DIGEST', label: 'Пробные занятия сегодня', kind: 'schedule' },
];

const EVENT_LABEL = Object.fromEntries(EVENT_TYPES.map((it) => [it.value, it.label]));
const EVENT_KIND = Object.fromEntries(EVENT_TYPES.map((it) => [it.value, it.kind]));

const CHANNELS = [{ value: 'telegram_owner', label: 'Telegram владельца' }];

const EMPTY_AUTOMATION = {
  name: '',
  event_type: 'FIRST_ATTENDANCE',
  allowed_from_system_types: ['lead', 'trial'],
  to_status_key: '',
  status_key: '',
  discount_percent: '',
  schedule_time: '09:00',
  timezone: 'Europe/Moscow',
  included_status_keys: [],
  channels: ['telegram_owner'],
  is_active: true,
};

/** Статусы, у которых включена работа с датой пробного — кандидаты для дайджеста. */
function isTrialStatus(item) {
  const mode = item?.value?.trial_date_mode;
  return mode === 'set_required' || mode === 'reschedule_required';
}

function statusTitle(item) {
  return item?.value?.name?.trim() || item?.key || '—';
}

function systemTypeOf(item) {
  return item?.value?.system_type ?? item?.value?.crm_logic ?? null;
}

function isActiveStatus(item) {
  return item?.value?.is_active !== false;
}

/**
 * Этапы воронки: какие статусы владельца показывать колонками Kanban и в каком порядке.
 * Порядок задаётся явно, потому что несколько статусов делят один system_type
 * («На пробное» и «Был на пробном» — оба trial), и он не задаёт их последовательность.
 */
function FunnelStagesTab() {
  const dispatch = useDispatch();
  const { items, status, saveError } = useSelector(selectSettingsGroup(STATUSES_GROUP));
  const [savingKey, setSavingKey] = useState(null);
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    if (status === 'idle') dispatch(fetchSettings(STATUSES_GROUP));
  }, [dispatch, status]);

  const activeItems = useMemo(() => {
    const list = items.filter(isActiveStatus);
    return [...list].sort((a, b) => {
      const ao = a.value?.funnel_order ?? Number.MAX_SAFE_INTEGER;
      const bo = b.value?.funnel_order ?? Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      return statusTitle(a).localeCompare(statusTitle(b), 'ru');
    });
  }, [items]);

  const orderOf = (item) =>
    drafts[item.key]?.funnel_order ?? (item.value?.funnel_order ?? '');

  const stageOf = (item) =>
    drafts[item.key]?.is_funnel_stage ?? Boolean(item.value?.is_funnel_stage);

  const setDraft = (key, patch) =>
    setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const save = async (item) => {
    const draft = drafts[item.key];
    if (!draft) return;
    setSavingKey(item.key);
    const rawOrder = draft.funnel_order ?? item.value?.funnel_order ?? '';
    const value = {
      ...(item.value ?? {}),
      is_funnel_stage: stageOf(item),
      funnel_order: rawOrder === '' || rawOrder === null ? null : Number(rawOrder),
    };
    const result = await dispatch(
      updateSettingItem({ group: STATUSES_GROUP, key: item.key, value }),
    );
    setSavingKey(null);
    if (result.meta.requestStatus === 'fulfilled') {
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[item.key];
        return next;
      });
    }
  };

  if (status === 'loading') return <div className="placeholder">Загрузка…</div>;

  return (
    <div>
      <p className="muted">
        Колонки рабочей доски формируются из этого списка. Технические и коммерческие статусы
        («Скидка 10», «Архив») обычно не нужны отдельными этапами продажи.
      </p>

      {saveError && <div className="banner banner--danger">{saveError}</div>}

      {activeItems.length === 0 ? (
        <div className="placeholder">Сначала заведите статусы клиентов</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Статус</th>
              <th style={{ width: 150 }}>Системный тип</th>
              <th style={{ width: 190 }}>Показывать в воронке</th>
              <th style={{ width: 140 }}>Порядок</th>
              <th style={{ width: 120 }} />
            </tr>
          </thead>
          <tbody>
            {activeItems.map((item) => {
              const dirty = Boolean(drafts[item.key]);
              return (
                <tr key={item.key}>
                  <td>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        background: item.value?.color ?? '#ccc',
                        marginRight: 8,
                      }}
                    />
                    {statusTitle(item)}
                  </td>
                  <td className="muted">
                    {SYSTEM_TYPE_LABEL[systemTypeOf(item)] ?? '—'}
                  </td>
                  <td>
                    <UiCheckbox
                      label="Этап воронки"
                      checked={stageOf(item)}
                      onChange={(e) =>
                        setDraft(item.key, { is_funnel_stage: e.target.checked })
                      }
                    />
                  </td>
                  <td>
                    <UiInput
                      type="number"
                      step={10}
                      value={orderOf(item)}
                      placeholder="напр. 40"
                      onChange={(e) => setDraft(item.key, { funnel_order: e.target.value })}
                    />
                  </td>
                  <td>
                    <UiButton
                      size="sm"
                      type="button"
                      disabled={!dirty || savingKey === item.key}
                      onClick={() => save(item)}
                    >
                      {savingKey === item.key ? '…' : 'Сохранить'}
                    </UiButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Автоматизации: событие + допустимые исходные system_type → конкретный целевой статус.
 * В настройке хранится item_key, поэтому переименование статуса правило не ломает.
 */
function FunnelAutomationsTab() {
  const dispatch = useDispatch();
  const automations = useSelector(selectSettingsGroup(AUTOMATIONS_GROUP));
  const statuses = useSelector(selectSettingsGroup(STATUSES_GROUP));

  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState(EMPTY_AUTOMATION);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (automations.status === 'idle') dispatch(fetchSettings(AUTOMATIONS_GROUP));
    if (statuses.status === 'idle') dispatch(fetchSettings(STATUSES_GROUP));
  }, [dispatch, automations.status, statuses.status]);

  const activeStatuses = useMemo(
    () => statuses.items.filter(isActiveStatus),
    [statuses.items],
  );

  const statusByKey = useMemo(
    () => new Map(statuses.items.map((it) => [it.key, it])),
    [statuses.items],
  );

  // Для дайджеста предлагаем только статусы ожидания пробного — остальные там не нужны.
  const trialStatusCandidates = useMemo(
    () => activeStatuses.filter(isTrialStatus),
    [activeStatuses],
  );

  const resetForm = () => {
    setEditingKey(null);
    setForm(EMPTY_AUTOMATION);
    setShowForm(false);
  };

  const onEdit = (item) => {
    setEditingKey(item.key);
    setForm({
      ...EMPTY_AUTOMATION,
      name: item.value?.name ?? '',
      event_type: item.value?.event_type ?? 'FIRST_ATTENDANCE',
      allowed_from_system_types: item.value?.allowed_from_system_types ?? [],
      to_status_key: item.value?.to_status_key ?? '',
      status_key: item.value?.status_key ?? '',
      discount_percent: item.value?.discount_percent ?? '',
      schedule_time: item.value?.schedule_time ?? '09:00',
      timezone: item.value?.timezone ?? 'Europe/Moscow',
      included_status_keys: item.value?.included_status_keys ?? [],
      channels: item.value?.channels ?? ['telegram_owner'],
      is_active: item.value?.is_active !== false,
    });
    setShowForm(true);
  };

  const toggleInList = (field, value) =>
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((it) => it !== value)
        : [...prev[field], value],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const value = { ...form, name: form.name.trim() };
    const thunk = editingKey
      ? updateSettingItem({ group: AUTOMATIONS_GROUP, key: editingKey, value })
      : createSettingItem({ group: AUTOMATIONS_GROUP, value });
    const result = await dispatch(thunk);
    if (result.meta.requestStatus === 'fulfilled') resetForm();
  };

  const onToggleActive = async (item) => {
    const value = { ...(item.value ?? {}), is_active: item.value?.is_active === false };
    await dispatch(updateSettingItem({ group: AUTOMATIONS_GROUP, key: item.key, value }));
  };

  const onDelete = async (item) => {
    if (!window.confirm(`Удалить автоматизацию «${item.value?.name ?? item.key}»?`)) return;
    await dispatch(deleteSettingItem({ group: AUTOMATIONS_GROUP, key: item.key }));
  };

  const rowActions = (item, enabled) => (
    <div className="row-actions">
      <UiButton variant="ghost" size="sm" type="button" onClick={() => onEdit(item)}>
        Изменить
      </UiButton>
      <UiButton variant="ghost" size="sm" type="button" onClick={() => onToggleActive(item)}>
        {enabled ? 'Выключить' : 'Включить'}
      </UiButton>
      <UiButton variant="danger" size="sm" type="button" onClick={() => onDelete(item)}>
        Удалить
      </UiButton>
    </div>
  );

  const eventItems = automations.items.filter(
    (it) => EVENT_KIND[it.value?.event_type] === 'event',
  );
  const discountItems = automations.items.filter(
    (it) => EVENT_KIND[it.value?.event_type] === 'status_discount',
  );
  const scheduleItems = automations.items.filter(
    (it) => EVENT_KIND[it.value?.event_type] === 'schedule',
  );

  const formKind = EVENT_KIND[form.event_type] ?? 'event';

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <p className="muted" style={{ order: -2 }}>
        Настройте реакцию на события CRM, расписание или постоянную скидку по статусу.
        Правила ссылаются на внутренний ключ статуса, поэтому переименование их не ломает.
      </p>

      {automations.saveError && (
        <div className="banner banner--danger" style={{ order: -2 }}>
          {automations.saveError}
        </div>
      )}

      <h3 className="settings-subtitle">Событийные</h3>
      {eventItems.length === 0 ? (
        <div className="placeholder">Событийных автоматизаций пока нет</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Автоматизация</th>
              <th>Событие</th>
              <th>Если текущий тип</th>
              <th>Установить статус</th>
              <th style={{ width: 90 }}>Состояние</th>
              <th style={{ width: 220 }} />
            </tr>
          </thead>
          <tbody>
            {eventItems.map((item) => {
              const v = item.value ?? {};
              const target = statusByKey.get(v.to_status_key);
              const broken = !target || target.value?.is_active === false;
              const enabled = v.is_active !== false;
              return (
                <tr key={item.key} style={enabled ? undefined : { opacity: 0.55 }}>
                  <td>{v.name ?? '—'}</td>
                  <td className="muted">{EVENT_LABEL[v.event_type] ?? v.event_type ?? '—'}</td>
                  <td className="muted">
                    {(v.allowed_from_system_types ?? [])
                      .map((t) => SYSTEM_TYPE_LABEL[t] ?? t)
                      .join(', ') || '—'}
                  </td>
                  <td>
                    {broken ? (
                      <span className="banner banner--danger" style={{ padding: '2px 8px' }}>
                        ⚠ Целевой статус недоступен
                      </span>
                    ) : (
                      <>
                        {statusTitle(target)}
                        <div className="muted" style={{ fontSize: 12 }}>
                          Тип: {SYSTEM_TYPE_LABEL[systemTypeOf(target)] ?? '—'}
                        </div>
                      </>
                    )}
                  </td>
                  <td className="muted">{enabled ? 'Активна' : 'Выключена'}</td>
                  <td>{rowActions(item, enabled)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <h3 className="settings-subtitle">Скидка по статусу</h3>
      <p className="muted">
        При установке выбранного статуса постоянная скидка клиента станет процентной.
        При снятии статуса будет установлен тип «Без скидки» и сумма 0. Если скидочный
        статус заменить другим, применится процент нового статуса.
      </p>
      {discountItems.length === 0 ? (
        <div className="placeholder">Автоматизаций скидки по статусу пока нет</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Автоматизация</th>
              <th>Статус</th>
              <th style={{ width: 160 }}>Постоянная скидка</th>
              <th style={{ width: 90 }}>Состояние</th>
              <th style={{ width: 220 }} />
            </tr>
          </thead>
          <tbody>
            {discountItems.map((item) => {
              const v = item.value ?? {};
              const target = statusByKey.get(v.status_key);
              const broken = !target || target.value?.is_active === false;
              const enabled = v.is_active !== false;
              return (
                <tr key={item.key} style={enabled ? undefined : { opacity: 0.55 }}>
                  <td>{v.name ?? '—'}</td>
                  <td>
                    {broken ? (
                      <span className="banner banner--danger" style={{ padding: '2px 8px' }}>
                        ⚠ Статус недоступен
                      </span>
                    ) : statusTitle(target)}
                  </td>
                  <td>{v.discount_percent ?? 0}%</td>
                  <td className="muted">{enabled ? 'Активна' : 'Выключена'}</td>
                  <td>{rowActions(item, enabled)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <h3 className="settings-subtitle">По расписанию</h3>
      {scheduleItems.length === 0 ? (
        <div className="placeholder">Автоматизаций по расписанию пока нет</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Автоматизация</th>
              <th>Событие</th>
              <th style={{ width: 160 }}>Когда</th>
              <th>Учитывать статусы</th>
              <th style={{ width: 160 }}>Канал</th>
              <th style={{ width: 90 }}>Состояние</th>
              <th style={{ width: 220 }} />
            </tr>
          </thead>
          <tbody>
            {scheduleItems.map((item) => {
              const v = item.value ?? {};
              const enabled = v.is_active !== false;
              const keys = v.included_status_keys ?? [];
              return (
                <tr key={item.key} style={enabled ? undefined : { opacity: 0.55 }}>
                  <td>{v.name ?? '—'}</td>
                  <td className="muted">{EVENT_LABEL[v.event_type] ?? v.event_type ?? '—'}</td>
                  <td className="muted">
                    Каждый день в {v.schedule_time ?? '09:00'}
                    <div style={{ fontSize: 12 }}>{v.timezone ?? 'Europe/Moscow'}</div>
                  </td>
                  <td className="muted">
                    {keys.length === 0
                      ? 'Все статусы с датой пробного'
                      : keys
                          .map((key) => statusTitle(statusByKey.get(key)) || key)
                          .join(', ')}
                  </td>
                  <td className="muted">
                    {(v.channels ?? [])
                      .map((c) => CHANNELS.find((it) => it.value === c)?.label ?? c)
                      .join(', ') || '—'}
                  </td>
                  <td className="muted">{enabled ? 'Активна' : 'Выключена'}</td>
                  <td>{rowActions(item, enabled)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {!showForm ? (
        <div style={{ order: -1, margin: '12px 0 16px' }}>
          <UiButton type="button" onClick={() => setShowForm(true)}>
            + Добавить автоматизацию
          </UiButton>
        </div>
      ) : (
        <div className="form-card" style={{ order: -1, margin: '12px 0 16px' }}>
          <h2>{editingKey ? 'Редактирование автоматизации' : 'Новая автоматизация'}</h2>
          <form onSubmit={onSubmit}>
            <UiInput
              label="Название"
              type="text"
              value={form.name}
              placeholder="Первое посещение"
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />

            <UiNativeSelect
              label="Тип автоматизации"
              value={form.event_type}
              onChange={(e) => setForm((p) => ({ ...p, event_type: e.target.value }))}
            >
              {EVENT_TYPES.map((it) => (
                <option key={it.value} value={it.value}>
                  {it.label}
                </option>
              ))}
            </UiNativeSelect>

            {formKind === 'event' ? (
              <>
                <div style={{ margin: '12px 0' }}>
                  <div className="muted" style={{ marginBottom: 6 }}>
                    Срабатывать, если текущий системный тип:
                  </div>
                  <div style={{ display: 'grid', gap: 6 }}>
                    {SYSTEM_TYPES.map((it) => (
                      <UiCheckbox
                        key={it.value}
                        label={`${it.label} (${it.value})`}
                        checked={form.allowed_from_system_types.includes(it.value)}
                        onChange={() => toggleInList('allowed_from_system_types', it.value)}
                      />
                    ))}
                  </div>
                </div>

                <UiNativeSelect
                  label="Установить статус"
                  value={form.to_status_key}
                  onChange={(e) => setForm((p) => ({ ...p, to_status_key: e.target.value }))}
                >
                  <option value="">— выберите статус —</option>
                  {activeStatuses.map((it) => (
                    <option key={it.key} value={it.key}>
                      {statusTitle(it)}
                      {systemTypeOf(it) ? ` — ${systemTypeOf(it)}` : ''}
                    </option>
                  ))}
                </UiNativeSelect>
              </>
            ) : formKind === 'status_discount' ? (
              <>
                <UiNativeSelect
                  label="Статус клиента"
                  value={form.status_key}
                  onChange={(e) => setForm((p) => ({ ...p, status_key: e.target.value }))}
                >
                  <option value="">— выберите статус —</option>
                  {activeStatuses.map((it) => (
                    <option key={it.key} value={it.key}>
                      {statusTitle(it)}
                    </option>
                  ))}
                </UiNativeSelect>

                <UiInput
                  label="Постоянная скидка, %"
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={form.discount_percent}
                  placeholder="Например, 10"
                  onChange={(e) =>
                    setForm((p) => ({ ...p, discount_percent: e.target.value }))
                  }
                />

                <p className="muted">
                  При установке статуса скидка станет процентной с указанным значением.
                  При снятии этого статуса установится «Без скидки» и значение 0.
                </p>
              </>
            ) : (
              <>
                <UiInput
                  label="Время отправки"
                  type="time"
                  value={form.schedule_time}
                  onChange={(e) => setForm((p) => ({ ...p, schedule_time: e.target.value }))}
                />
                <UiInput
                  label="Часовой пояс"
                  type="text"
                  value={form.timezone}
                  placeholder="Europe/Moscow"
                  onChange={(e) => setForm((p) => ({ ...p, timezone: e.target.value }))}
                />

                <div style={{ margin: '12px 0' }}>
                  <div className="muted" style={{ marginBottom: 6 }}>
                    Учитывать статусы. Если ничего не выбрать, возьмём все статусы, у которых
                    включена работа с датой пробного.
                  </div>
                  <div style={{ display: 'grid', gap: 6 }}>
                    {trialStatusCandidates.map((it) => (
                      <UiCheckbox
                        key={it.key}
                        label={statusTitle(it)}
                        checked={form.included_status_keys.includes(it.key)}
                        onChange={() => toggleInList('included_status_keys', it.key)}
                      />
                    ))}
                  </div>
                </div>

                <div style={{ margin: '12px 0' }}>
                  <div className="muted" style={{ marginBottom: 6 }}>
                    Канал доставки:
                  </div>
                  <div style={{ display: 'grid', gap: 6 }}>
                    {CHANNELS.map((it) => (
                      <UiCheckbox
                        key={it.value}
                        label={it.label}
                        checked={form.channels.includes(it.value)}
                        onChange={() => toggleInList('channels', it.value)}
                      />
                    ))}
                  </div>
                </div>

                <p className="muted">
                  Если на выбранную дату пробных нет, сообщение не отправляется. Повторно за один
                  день дайджест не уходит.
                </p>
              </>
            )}

            <div style={{ margin: '12px 0' }}>
              <UiCheckbox
                label="Автоматизация включена"
                checked={form.is_active}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              />
            </div>

            <div className="form-actions">
              <UiButton variant="ghost" type="button" onClick={resetForm}>
                Отмена
              </UiButton>
              <UiButton type="submit" disabled={automations.saveStatus === 'loading'}>
                {editingKey ? 'Сохранить' : 'Добавить'}
              </UiButton>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/**
 * Виджет «Пробные сегодня» на главной CRM (ТЗ «Дата пробного», §28–§30).
 *
 * Кому показывать виджет, задаётся правом `dashboard.trials_today.view` в
 * «Настройки CRM → Доступы администраторов»: там уже есть матрица админов и прав,
 * и дублировать её отдельным списком ID значило бы держать два источника правды.
 */
function TrialsWidgetTab() {
  const dispatch = useDispatch();
  const widgets = useSelector(selectSettingsGroup(WIDGETS_GROUP));
  const statuses = useSelector(selectSettingsGroup(STATUSES_GROUP));
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (widgets.status === 'idle') dispatch(fetchSettings(WIDGETS_GROUP));
    if (statuses.status === 'idle') dispatch(fetchSettings(STATUSES_GROUP));
  }, [dispatch, widgets.status, statuses.status]);

  const saved = useMemo(() => {
    const item = widgets.items.find((it) => it.key === TRIALS_WIDGET_KEY);
    return {
      enabled: item?.value?.enabled !== false,
      visible_to_owner: item?.value?.visible_to_owner !== false,
      included_status_keys: item?.value?.included_status_keys ?? [],
    };
  }, [widgets.items]);

  const form = draft ?? saved;

  const trialStatuses = useMemo(
    () => statuses.items.filter(isActiveStatus).filter(isTrialStatus),
    [statuses.items],
  );

  const toggleStatus = (key) =>
    setDraft({
      ...form,
      included_status_keys: form.included_status_keys.includes(key)
        ? form.included_status_keys.filter((it) => it !== key)
        : [...form.included_status_keys, key],
    });

  const onSave = async () => {
    setSaving(true);
    const result = await dispatch(
      updateSettingItem({ group: WIDGETS_GROUP, key: TRIALS_WIDGET_KEY, value: form }),
    );
    setSaving(false);
    if (result.meta.requestStatus === 'fulfilled') setDraft(null);
  };

  if (widgets.status === 'loading') return <div className="placeholder">Загрузка…</div>;

  return (
    <div className="form-card" style={{ maxWidth: 620 }}>
      <h2>Виджет «Пробные сегодня»</h2>
      <p className="muted">
        Показывает администратору список клиентов, записанных на пробное на сегодня, и
        обновляется в течение дня.
      </p>

      {widgets.saveError && <div className="banner banner--danger">{widgets.saveError}</div>}

      <div style={{ display: 'grid', gap: 8, margin: '12px 0' }}>
        <UiCheckbox
          label="Виджет включён"
          checked={form.enabled}
          onChange={(e) => setDraft({ ...form, enabled: e.target.checked })}
        />
        <UiCheckbox
          label="Показывать виджет владельцу"
          checked={form.visible_to_owner}
          onChange={(e) => setDraft({ ...form, visible_to_owner: e.target.checked })}
        />
      </div>

      <div style={{ margin: '12px 0' }}>
        <div className="muted" style={{ marginBottom: 6 }}>
          Учитывать статусы. Если ничего не выбрать, возьмём все статусы с включённой работой
          по дате пробного.
        </div>
        {trialStatuses.length === 0 ? (
          <div className="placeholder">
            Ни у одного статуса не включена работа с датой пробного. Настройте это в разделе
            «Статусы клиентов».
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {trialStatuses.map((it) => (
              <UiCheckbox
                key={it.key}
                label={statusTitle(it)}
                checked={form.included_status_keys.includes(it.key)}
                onChange={() => toggleStatus(it.key)}
              />
            ))}
          </div>
        )}
      </div>

      <p className="muted">
        Кому показывать виджет, настраивается правом «Пробные сегодня» в разделе CRM
        «Настройки → Доступы администраторов». Администратор видит только клиентов тех
        филиалов, к которым у него есть доступ.
      </p>

      <div className="form-actions">
        <UiButton type="button" disabled={!draft || saving} onClick={onSave}>
          {saving ? '…' : 'Сохранить'}
        </UiButton>
      </div>
    </div>
  );
}

const SUB_TABS = [
  { key: 'stages', label: 'Этапы' },
  { key: 'automations', label: 'Автоматизации' },
  { key: 'widget', label: 'Виджет «Пробные сегодня»' },
];

export function SalesFunnelSettings() {
  const [active, setActive] = useState('stages');

  return (
    <div>
      <div className="tabs">
        {SUB_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={tab.key === active ? 'active' : ''}
            onClick={() => setActive(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {active === 'stages' && <FunnelStagesTab />}
      {active === 'automations' && <FunnelAutomationsTab />}
      {active === 'widget' && <TrialsWidgetTab />}
    </div>
  );
}
